/**
 * API Service - Axios instance with interceptors
 * 
 * Features:
 * - Auto-refresh tokens before expiry (proactive refresh)
 * - Queue failed requests during token refresh
 * - Redirect to login on auth failure
 */

import {
  clearImpersonationStash,
  readImpersonationStash,
} from './impersonation-stash';
import axios from 'axios';

const TOKEN_KEY = 'nord-living-token';
const REFRESH_KEY = 'nord-living-refresh';
const TOKEN_EXPIRY_KEY = 'nord-living-token-expiry';
const CORRELATION_ID_KEY = 'nord-living-correlation-id';

// API URL: set to backend origin, or leave empty to use same origin as the Vite dev server (LAN-friendly).
const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
const API_BASE =
  envApiUrl === '' ? '' : (envApiUrl ?? 'http://localhost:4601');

export const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 45_000,
});

function generateCorrelationId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback: RFC4122-ish
  return `cid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function getCorrelationId(): string {
  const storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  const existing = storage?.getItem(CORRELATION_ID_KEY);
  if (existing) return existing;

  const correlationId = generateCorrelationId();
  storage?.setItem(CORRELATION_ID_KEY, correlationId);
  return correlationId;
}

// Track if we're currently refreshing to prevent multiple refresh calls
let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (error: unknown) => void }[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

/**
 * Clear auth storage and redirect to login.
 * Call this when token is expired/invalid and refresh failed (or unavailable).
 * Exception: an expiring impersonation session restores the stashed admin
 * tokens instead of logging out (impersonation tokens have no refresh token).
 */
/** True when the server refused the refresh token itself (not a transient failure). */
export function isRefreshRejected(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === 400 || status === 401 || status === 403;
}

export const clearAuthAndRedirect = () => {
  const stash = readImpersonationStash();
  if (stash) {
    clearImpersonationStash();
    localStorage.setItem(TOKEN_KEY, stash.accessToken);
    localStorage.setItem(REFRESH_KEY, stash.refreshToken);
    localStorage.setItem(TOKEN_EXPIRY_KEY, stash.tokenExpiry);
    window.location.reload();
    return;
  }
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  window.dispatchEvent(new Event('nord-living:auth-cleared'));
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
};

/**
 * Store token with expiry time
 */
export const setAuthTokens = (
  accessToken: string, 
  refreshToken: string, 
  expiresIn: number
) => {
  const expiryTime = Date.now() + (expiresIn * 1000);
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
  window.dispatchEvent(
    new CustomEvent('nord-living:tokens-updated', { detail: { expiryTime } }),
  );
};

/**
 * Check if token is about to expire (within 2 minutes)
 */
const isTokenExpiringSoon = (): boolean => {
  const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
  // If expiry isn't available, don't proactively refresh.
  // We'll rely on 401 refresh flow instead.
  if (!expiryStr) return false;
  
  const expiry = parseInt(expiryStr, 10);
  const twoMinutes = 2 * 60 * 1000;
  
  return Date.now() > (expiry - twoMinutes);
};

/**
 * Proactively refresh token if it's about to expire
 */
const proactiveRefresh = async (): Promise<string | null> => {
  if (isRefreshing) return null;
  
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken || !isTokenExpiringSoon()) return null;

  isRefreshing = true;

  try {
    const response = await axios.post(`${API_BASE}/api/auth/refresh`, { refreshToken }, { timeout: 15_000 });
    const { accessToken, refreshToken: newRefresh, expiresIn } = response.data.data;

    setAuthTokens(accessToken, newRefresh, expiresIn || 900);
    api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    
    return accessToken;
  } catch {
    // Don't clear auth here - let the 401 handler deal with it
    return null;
  } finally {
    isRefreshing = false;
  }
};

// Request interceptor - add auth token and proactively refresh if needed
api.interceptors.request.use(
  async (config) => {
    config.headers = config.headers ?? {};
    if (!config.headers['X-Correlation-Id'] && !config.headers['x-correlation-id']) {
      config.headers['X-Correlation-Id'] = getCorrelationId();
    }

    // Skip auth for login/register/refresh endpoints
    if (
      config.url?.includes('/auth/login') ||
      config.url?.includes('/auth/register') ||
      config.url?.includes('/auth/refresh')
    ) {
      return config;
    }

    // Proactively refresh if token is expiring soon
    const newToken = await proactiveRefresh();
    
    const token = newToken || localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor - handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for auth endpoints
      if (
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/refresh')
      ) {
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem(REFRESH_KEY);

      // No refresh token - redirect to login
      if (!refreshToken) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${API_BASE}/api/auth/refresh`, { refreshToken }, { timeout: 15_000 });
        const { accessToken, refreshToken: newRefresh, expiresIn } = response.data.data;

        setAuthTokens(accessToken, newRefresh, expiresIn || 900);
        api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        processQueue(null, accessToken);

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Only a rejected refresh token ends the session; a network error,
        // rate limit or server restart keeps it (the next request retries).
        if (isRefreshRejected(refreshError)) clearAuthAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

/** For fetch-based streams (e.g. SSE) that need auth and base URL */
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getApiBaseUrl(): string {
  return API_BASE;
}

export default api;
